import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Zap, Shield, Settings, Headphones, Check, Star, Lock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO, { jsonLdSoftwareApp } from '../components/SEO';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="ACBotHub — Chatbots de WhatsApp con IA para tu negocio"
        description="Crea asistentes de WhatsApp con inteligencia artificial. Atiende clientes 24/7, agenda citas y toma pedidos. Prueba gratis."
        path="/"
        jsonLd={jsonLdSoftwareApp}
      />
      <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-md fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-indigo-600" />
            <span className="text-xl font-bold tracking-tight text-gray-950">ACBotHub</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" data-testid="nav-login-btn" className="text-gray-700 hover:text-gray-950">
                Iniciar Sesión
              </Button>
            </Link>
            <Link to="/register">
              <Button data-testid="nav-register-btn" className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md hover:shadow-lg transition-all rounded-lg font-medium px-6 py-2.5">
                Comenzar
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-bold tracking-tight text-gray-950 mb-6">
            Crea tu Propio <span className="gradient-text">Asistente de WhatsApp</span>
            <br />Sin Escribir Código
          </h1>
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
            Construye, administra y despliega asistentes profesionales de WhatsApp en minutos. No se requiere conocimiento tecnico. Solo conecta, configura y activa.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/register">
              <Button data-testid="hero-get-started-btn" size="lg" className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg hover:shadow-xl transition-all rounded-lg font-semibold px-8 py-6 text-lg">
                Comenzar Ahora
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Elige el tipo de asistente — showcase de kits */}
      <section className="py-20 px-6" data-testid="kit-selector-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start gap-4 mb-12">
            <div className="w-12 h-12 rounded-2xl bg-indigo-100 flex items-center justify-center shrink-0 mt-1">
              <svg viewBox="0 0 24 24" className="w-6 h-6 text-indigo-600 fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 8V4H8" /><rect x="4" y="8" width="16" height="12" rx="2" /><path d="M2 14h2M20 14h2M15 13v2M9 13v2" />
              </svg>
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 mb-2">
                Elige el tipo de asistente
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Selecciona la plantilla que mejor se adapte a tu negocio.<br />
                Podrás configurarlo en detalle después.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-4 md:gap-5">
            {[
              {
                slug: 'general',
                to: '/register',
                title: 'Asistente General',
                icon: '/kit_icon_general.png',
                badge: 'PERSONALIZABLE',
                badgeBg: 'bg-indigo-100', badgeText: 'text-indigo-700',
                arrowBg: 'bg-indigo-600 hover:bg-indigo-700',
                cardBg: 'bg-gradient-to-b from-indigo-50/60 to-white',
                desc: 'Bot para cualquier negocio: tiendas, servicios, restaurantes, profesionales. Configura tu prompt y conecta WhatsApp.',
              },
              {
                slug: 'autobot',
                to: '/kits/autobot',
                title: 'Kit AutoBot',
                icon: '/kit_icon_auto.png',
                badge: 'CONCESIONARIAS',
                badgeBg: 'bg-orange-100', badgeText: 'text-orange-700',
                arrowBg: 'bg-orange-500 hover:bg-orange-600',
                cardBg: 'bg-gradient-to-b from-orange-50/60 to-white',
                desc: 'Plantilla para concesionarias: captura leads, score automático, agenda pruebas de manejo y notifica por email.',
              },
              {
                slug: 'garzonbot',
                to: '/kits/garzonbot',
                title: 'GarzónBot',
                icon: '/logo_garzonbot.png',
                iconClass: 'w-20 h-20 md:w-24 md:h-24',
                badge: 'RESTAURANTES',
                badgeBg: 'bg-emerald-100', badgeText: 'text-emerald-700',
                arrowBg: 'bg-emerald-600 hover:bg-emerald-700',
                cardBg: 'bg-gradient-to-b from-emerald-50/60 to-white',
                desc: 'Asistente para bares y restaurantes: toma pedidos por mesa vía QR y envía comandas a Google Sheets.',
              },
              {
                slug: 'inmobot',
                to: '/kits/inmobot',
                title: 'Kit Inmobiliario',
                icon: '/kit_icon_inmo.png',
                badge: 'CORREDORAS',
                badgeBg: 'bg-indigo-100', badgeText: 'text-indigo-700',
                arrowBg: 'bg-indigo-600 hover:bg-indigo-700',
                cardBg: 'bg-gradient-to-b from-indigo-50/60 to-white',
                desc: 'Plantilla para corredoras: distingue buscadores y propietarios, agenda visitas y clasifica por temperatura del lead.',
              },
              {
                slug: 'legalbot',
                to: '/kits/legalbot',
                title: 'Kit Legal',
                icon: '/kit_icon_legal.png',
                badge: 'ESTUDIOS JURÍDICOS',
                badgeBg: 'bg-indigo-100', badgeText: 'text-indigo-700',
                arrowBg: 'bg-indigo-600 hover:bg-indigo-700',
                cardBg: 'bg-gradient-to-b from-indigo-50/60 to-white',
                desc: 'Para estudios jurídicos: agenda clientes, detecta audiencias PJUD automáticamente y las agrega a Google Calendar.',
              },
              {
                slug: 'storebot',
                to: '/kits/storebot',
                title: 'Kit StoreBot',
                icon: '/kit_icon_store.png',
                badge: 'TIENDAS CON CATÁLOGO',
                badgeBg: 'bg-sky-100', badgeText: 'text-sky-700',
                arrowBg: 'bg-sky-600 hover:bg-sky-700',
                cardBg: 'bg-gradient-to-b from-sky-50/60 to-white',
                desc: 'Para showrooms, mueblerías y tiendas: catálogo 24/7 por WhatsApp, captura leads y envía notificación inmediata al equipo.',
              },
            ].map((k) => (
              <Link
                key={k.slug}
                to={k.to}
                data-testid={`kit-selector-${k.slug}`}
                className={`group relative flex flex-col items-center text-center rounded-3xl border border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all p-6 ${k.cardBg}`}
              >
                {/* Fixed-height slot keeps titles aligned across cards even
                    when an icon has a custom smaller size (e.g. GarzónBot). */}
                <div className="w-28 h-28 md:w-32 md:h-32 mb-5 flex items-center justify-center shrink-0">
                  <img
                    src={k.icon}
                    alt={`${k.title} icon`}
                    className={`${k.iconClass || 'w-28 h-28 md:w-32 md:h-32'} object-contain`}
                    loading="lazy"
                  />
                </div>
                <h3 className="text-lg font-bold text-indigo-950 mb-3">{k.title}</h3>
                <div className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-[10px] font-bold tracking-wider ${k.badgeBg} ${k.badgeText} mb-4`}>
                  {k.badge}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-6 min-h-[100px]">{k.desc}</p>
                <div className={`w-11 h-11 rounded-full ${k.arrowBg} text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform mt-auto`}>
                  <ArrowRight className="w-5 h-5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 text-center mb-16">
            Todo lo que Necesitas para Tener Éxito
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-950 mb-3">Configuración Rápida</h3>
              <p className="text-gray-600 leading-relaxed">
                Ten tu asistente funcionando en menos de 5 minutos. Simplemente escanea un codigo QR y estaras conectado.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <Settings className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-950 mb-3">Configuración Fácil</h3>
              <p className="text-gray-600 leading-relaxed">
                Personaliza mensajes de bienvenida y respuestas automáticas con nuestra interfaz simple y amigable.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-6">
                <Shield className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-950 mb-3">Seguro y Confiable</h3>
              <p className="text-gray-600 leading-relaxed">
                Tus datos están protegidos con seguridad de nivel empresarial. 99.9% de tiempo de actividad garantizado.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-indigo-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 mb-6">
            <Headphones className="w-4 h-4 text-indigo-100" />
            <span className="text-indigo-100 text-sm font-medium">Soporte en vivo</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
            No sabes como configurar tu asistente?
          </h2>
          <p className="text-lg text-indigo-100 mb-8 max-w-2xl mx-auto leading-relaxed">
            Habla con Alex y el te crea las instrucciones gratis. Te guiara paso a paso para generar el prompt perfecto para tu negocio.
          </p>
          <Button
            data-testid="cta-talk-to-alex"
            onClick={() => window.dispatchEvent(new Event('openSupportChat'))}
            size="lg"
            className="bg-white text-indigo-700 hover:bg-indigo-50 shadow-lg hover:shadow-xl transition-all rounded-lg font-semibold px-8 py-6 text-lg"
          >
            Hablar con Alex
          </Button>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6 bg-gray-50" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 mb-4">
              Planes simples y transparentes
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Todos incluyen prueba gratis. Sin contratos, cancela cuando quieras.
            </p>
          </div>

          {/* 3 main plans */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto items-start">
            {/* BÁSICO */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow" data-testid="plan-basic">
              <h3 className="text-lg font-semibold text-gray-950 mb-1">Básico</h3>
              <p className="text-sm text-gray-500 mb-6">Para emprendedores</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-950">$24</span>
                <span className="text-gray-500 text-sm">/mes</span>
              </div>
              <ul className="space-y-2.5 mb-6">
                {[
                  '1 asistente WhatsApp con IA',
                  'Dashboard de gestión',
                  'Conexión WhatsApp vía QR',
                  'Widget embebible en tu web',
                  'Historial 7 días',
                  'Soporte por email',
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />{f}
                  </li>
                ))}
                <li className="flex items-start gap-2.5 text-sm text-gray-500">
                  <Lock className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                  <span>Sin kit de industria</span>
                </li>
              </ul>
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 mb-6">
                <p className="text-xs text-indigo-700 font-medium text-center">Prueba 7 días gratis</p>
              </div>
              <Link to="/register">
                <Button variant="outline" data-testid="plan-basic-btn"
                  className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-5 font-medium">
                  Empezar prueba gratis
                </Button>
              </Link>
            </div>

            {/* PRO */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow" data-testid="plan-pro">
              <h3 className="text-lg font-semibold text-gray-950 mb-1">Pro</h3>
              <p className="text-sm text-gray-500 mb-6">Para negocios en crecimiento</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-950">$49</span>
                <span className="text-gray-500 text-sm">/mes</span>
              </div>
              <ul className="space-y-2.5 mb-6">
                {[
                  '3 asistentes activos',
                  'Historial completo',
                  'Integración Google Calendar',
                  'Reagendamiento automático',
                  'Widget embebible',
                  'Soporte prioritario',
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />{f}
                  </li>
                ))}
                <li className="flex items-start gap-2.5 text-sm text-gray-500">
                  <Lock className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                  <span>Sin kit de industria</span>
                </li>
              </ul>
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 mb-6">
                <p className="text-xs text-indigo-700 font-medium text-center">Prueba 14 días gratis</p>
              </div>
              <Link to="/register">
                <Button variant="outline" data-testid="plan-pro-btn"
                  className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-5 font-medium">
                  Empezar prueba gratis
                </Button>
              </Link>
            </div>

            {/* BUSINESS - Destacado */}
            <div className="bg-white border-2 border-indigo-600 rounded-2xl p-8 shadow-lg relative" data-testid="plan-business">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                <span className="inline-flex items-center gap-1.5 bg-indigo-600 text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-md">
                  <Star className="w-3 h-3 fill-current" />Más popular
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-950 mb-1">Business</h3>
              <p className="text-sm text-gray-500 mb-6">Para negocios con kit de industria</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-950">$199</span>
                <span className="text-gray-500 text-sm">/mes</span>
              </div>
              <ul className="space-y-2.5 mb-6">
                {[
                  '10 asistentes activos',
                  'Todo lo del plan Pro',
                  'Pasarelas de pago (Flow, Klap, PayPal)',
                  'Carrito abandonado automático',
                  'Widget con branding personalizado',
                  'Soporte VIP + onboarding',
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />{f}
                  </li>
                ))}
                <li className="flex items-start gap-2.5 text-sm text-gray-950 font-semibold bg-indigo-50 border border-indigo-100 rounded-lg px-2.5 py-2">
                  <Check className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                  <span>1 kit de industria incluido (elige al activar)</span>
                </li>
              </ul>
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 mb-6">
                <p className="text-xs text-indigo-700 font-medium text-center">Prueba 14 días gratis · Onboarding incluido</p>
              </div>
              <Link to="/register">
                <Button data-testid="plan-business-btn"
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-5 font-medium shadow-md hover:shadow-lg transition-all">
                  Empezar prueba gratis
                </Button>
              </Link>
            </div>
          </div>

          {/* Kits add-on */}
          <div className="max-w-6xl mx-auto mt-14" data-testid="kits-addon-section">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                  <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-semibold mb-2">
                    <Star className="w-3 h-3" /> Add-on Business
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-950">Kits de industria</h3>
                  <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                    Elige 1 kit incluido en tu plan Business. Los otros 4 aparecen con candado en el dashboard y se activan pagando <strong className="text-gray-950">+$99/mes</strong> cada uno.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {[
                  { slug: 'garzonbot', name: 'GarzónBot', logo: '/logo_garzonbot.png', desc: 'Restaurantes' },
                  { slug: 'autobot', name: 'AutoBot', emoji: '🚗', desc: 'Concesionarias' },
                  { slug: 'inmobot', name: 'InmoBot', emoji: '🏠', desc: 'Inmobiliarias' },
                  { slug: 'legalbot', name: 'LegalBot', emoji: '⚖️', desc: 'Estudios jurídicos' },
                  { slug: 'storebot', name: 'StoreBot', emoji: '🛍️', desc: 'Tiendas / showrooms' },
                ].map((k) => (
                  <Link
                    key={k.slug}
                    to={`/kits/${k.slug}`}
                    data-testid={`kit-card-${k.slug}`}
                    className="group bg-gray-50 hover:bg-white border border-gray-200 hover:border-indigo-300 rounded-xl p-4 transition-all hover:shadow-md"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      {k.logo ? (
                        <img src={k.logo} alt={`${k.name} logo`} className="w-8 h-8 object-contain shrink-0" />
                      ) : (
                        <span className="text-2xl" aria-hidden>{k.emoji}</span>
                      )}
                      <div className="font-semibold text-gray-950 text-sm">{k.name}</div>
                    </div>
                    <div className="text-xs text-gray-500">{k.desc}</div>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 group-hover:text-indigo-700">
                      Ver detalles <ArrowRight className="w-3 h-3" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Partners note */}
          <div className="max-w-6xl mx-auto mt-6 text-center" data-testid="partners-cta">
            <p className="text-sm text-gray-600">
              ¿Eres agencia o consultor?{' '}
              <Link to="/register-partner" className="text-indigo-600 hover:text-indigo-700 font-semibold">
                Únete al programa Partners →
              </Link>
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 mb-6">
            ¿Listo para Transformar tu Negocio?
          </h2>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed">
            Unete a miles de empresas que automatizan su atencion al cliente con asistentes de WhatsApp.
          </p>
          <Link to="/register">
            <Button data-testid="cta-get-started-btn" size="lg" className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg hover:shadow-xl transition-all rounded-lg font-semibold px-8 py-6 text-lg">
              Comenzar Gratis
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-gray-200 py-8 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-3">
          <div className="flex items-center gap-6 text-sm">
            <Link to="/terms" className="text-gray-500 hover:text-gray-700 transition-colors">Terminos y Condiciones</Link>
            <Link to="/privacy" className="text-gray-500 hover:text-gray-700 transition-colors">Politica de Privacidad</Link>
          </div>
          <p className="text-gray-400 text-sm">&copy; 2026 ACBotHub. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
